<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useAvatarStore } from 'stores/avatarStore';
import { useEventStore } from 'src/stores/event';
import {
  AVATAR_CONFIG_VERSION,
  defaultAvatarOptions,
  isReplayableAvatarConfig,
  type AvatarConfig,
} from 'src/modules/Avatar/types/avatarConfig';
import {
  mdiShuffle,
  mdiDownload,
  mdiImage,
  mdiXml,
  mdiContentSave,
  mdiRestore,
} from '@quasar/extras/mdi-v7';
import type { AvatarCombination } from '../../services/avatarCombinations';
import { Avatar } from 'src/modules/Avatar/models/Avatar';
import { useRepo } from 'pinia-orm';

interface Props {
  isSavingProfile?: boolean;
}

withDefaults(defineProps<Props>(), {
  isSavingProfile: false,
});

const avatarRepo = useRepo(Avatar);
const store = useAvatarStore();
const eventStore = useEventStore();
let unsubscribeConfigLoaded: (() => void) | null = null;

const previewRef = ref<HTMLElement>();
const tabsRef = ref<HTMLElement>();
const error = ref('');

// The selected style's definition is fetched on demand.
const isLoading = computed(() => !store.isReady);

/** The avatar currently stored on the user's profile, if any. */
const savedConfig = computed(
  () => avatarRepo.query().first()?.avatar_config ?? null,
);

/**
 * Only offer the restore action when the saved avatar can actually be replayed:
 * configs written before DiceBear 10 use option names v10 rejects.
 */
const canRestoreSaved = computed(() => isReplayableAvatarConfig(savedConfig.value));

function sameSelection(
  a: Record<string, unknown>,
  b: Record<string, unknown>,
): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  return [...keys].every((key) => a[key] === b[key]);
}

/**
 * Whether the editor differs from what is already on the profile. Saving an
 * identical avatar would just write the same record again.
 */
const hasUnsavedChanges = computed(() => {
  const saved = savedConfig.value;
  if (!isReplayableAvatarConfig(saved)) return true;

  return (
    saved.style !== store.selectedStyleName ||
    saved.seed !== store.seed ||
    !sameSelection(saved.styleOptions, store.currentStyleOptions)
  );
});

/**
 * Applies a saved config to the editor. Configs written before DiceBear 10 use
 * option names that v10 rejects, so they are skipped — the editor then starts
 * from its defaults while the stored SVG keeps rendering elsewhere.
 */
function applyConfig(config: AvatarConfig | null | undefined) {
  if (!isReplayableAvatarConfig(config)) return;

  if (config.style) {
    store.changeStyle(config.style);
  }
  if (config.seed) {
    store.seed = config.seed;
  }
  if (config.styleOptions) {
    store.updateOptions({ ...config.styleOptions } as Record<string, string>);
  }
}

/** Discards the current edits and goes back to the saved avatar. */
function restoreSaved() {
  applyConfig(savedConfig.value);
}

/**
 * Open the editor on the avatar the user already has.
 *
 * `users/me` populates the avatar record asynchronously, so it is often absent
 * when this view mounts — and the `avatar:config-loaded` event is emitted from
 * the parent page's setup, before this view can subscribe. Watching the record
 * catches it whenever it lands. Only the first arrival is applied, so saving
 * (which updates the record) does not yank the editor back a second time.
 */
const hasLoadedSaved = ref(false);

watch(
  savedConfig,
  (config) => {
    if (hasLoadedSaved.value || !isReplayableAvatarConfig(config)) return;

    try {
      hasLoadedSaved.value = true;
      applyConfig(config);
    } catch (e) {
      console.error('Error applying stored avatar config:', e);
      error.value = e instanceof Error ? e.message : 'Unknown error occurred';
    }
  },
  { immediate: true },
);

onMounted(() => {
  // Other flows may load a config into the editor too.
  unsubscribeConfigLoaded = eventStore.on(
    'avatar:config-loaded',
    (payload: { config: AvatarConfig }) => {
      applyConfig(payload?.config);
    },
  );
});

onUnmounted(() => {
  if (unsubscribeConfigLoaded) {
    unsubscribeConfigLoaded();
    unsubscribeConfigLoaded = null;
  }
});

// Get all option keys
const optionKeys = computed(() => {
  return ['style', ...Object.keys(store.optionCombinations)];
});

// Check if option has multiple values
function hasMultipleOptions(key: string): boolean {
  if (key === 'style') {
    return store.availableStyles.length > 1;
  }
  const options = store.optionCombinations[key] || [];
  return options.length > 1;
}

// Format option key to readable label
function formatLabel(key: string): string {
  if (key === 'style') return 'Style';
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
}

// Handle option selection
function handleOptionClick(combination: AvatarCombination) {
  if (!combination.isCustomColor) {
    store.updateOptions(combination.options);
  }
}

// Handle color change
function handleColorChange(key: string, value: string) {
  const cleanValue = value.replace('#', '');
  store.updateOptions({
    ...store.currentStyleOptions,
    [key]: cleanValue,
  });
}

// Download avatar as PNG (render SVG to canvas, then to PNG)
async function downloadPNG() {
  try {
    const svg = store.currentAvatar?.toString();
    if (!svg) return;

    // Create image from SVG
    const img = new Image();
    const svgBlob = new Blob([svg], { type: 'image/svg+xml' });
    const svgUrl = URL.createObjectURL(svgBlob);

    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('Failed to load SVG image'));
      img.src = svgUrl;
    });

    const canvas = document.createElement('canvas');
    canvas.width = img.width || 512;
    canvas.height = img.height || 512;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Canvas context not available');
    }
    ctx.drawImage(img, 0, 0);

    URL.revokeObjectURL(svgUrl);

    canvas.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `avatar-${Date.now()}.png`;
      link.click();
      URL.revokeObjectURL(url);
    }, 'image/png');
  } catch (e) {
    console.error('Failed to download PNG', e);
  }
}

// Download avatar as raw SVG
function downloadSVG() {
  try {
    const svg = store.currentAvatar?.toString();
    if (!svg) return;

    const svgBlob = new Blob([svg], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(svgBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `avatar-${Date.now()}.svg`;
    link.click();
    URL.revokeObjectURL(url);
  } catch (e) {
    console.error('Failed to download SVG', e);
  }
}

// Handle save to strapi
function handleSaveAvatar() {
  const currentAvatar = store.currentAvatar;
  if (!currentAvatar) return;

  const config: AvatarConfig = {
    version: AVATAR_CONFIG_VERSION,
    style: store.selectedStyleName,
    seed: store.seed,
    options: {
      ...defaultAvatarOptions,
      style: store.selectedStyleName,
      seed: store.seed,
    },
    styleOptions: { ...store.currentStyleOptions },
    svg: currentAvatar.toString(),
    dataUri: currentAvatar.toDataUri(),
  };

  eventStore.emit('avatar:save-requested', { config });
}
</script>

<!-- eslint-disable vue/no-v-html -->
<template>
  <!-- Loading State -->
  <div
    v-if="isLoading"
    class="loading-state"
  >
    <q-spinner-rings
      color="white"
      size="80px"
    />
    <div class="text-white q-mt-md">{{ $t('LOADING...') }}</div>
  </div>

  <!-- Error State -->
  <div
    v-else-if="error"
    class="error-state"
  >
    <q-icon
      name="error"
      color="white"
      size="80px"
    />
    <div class="text-white q-mt-md">{{ error }}</div>
  </div>

  <!-- Main Content -->
  <template v-else>
    <!-- Sticky Header -->
    <q-toolbar class="editor-toolbar absolute q-pt-sm q-px-md">
      <!-- Editing actions -->
      <div class="toolbar-group">
        <q-btn
          round
          flat
          :icon="mdiShuffle"
          color="white"
          size="md"
          @click="store.randomize()"
        >
          <q-tooltip>{{ $t('AVATAR_RANDOMIZE') }}</q-tooltip>
        </q-btn>
        <q-btn
          v-if="canRestoreSaved"
          round
          flat
          :icon="mdiRestore"
          color="white"
          size="md"
          :disable="!hasUnsavedChanges"
          @click="restoreSaved"
        >
          <q-tooltip>{{ $t('AVATAR_RESTORE_SAVED') }}</q-tooltip>
        </q-btn>
      </div>

      <q-space />

      <!-- Output actions -->
      <div class="toolbar-group">
        <q-fab
          color="white"
          :icon="mdiDownload"
          direction="down"
          padding="9px"
          flat
        >
          <q-fab-action
            color="primary"
            :icon="mdiImage"
            :label="$t('AVATAR_DOWNLOAD_PNG')"
            @click="downloadPNG"
          />
          <q-fab-action
            color="primary"
            :icon="mdiXml"
            :label="$t('AVATAR_DOWNLOAD_SVG')"
            @click="downloadSVG"
          />
        </q-fab>
        <q-btn
          round
          unelevated
          :icon="mdiContentSave"
          color="primary"
          text-color="dark"
          size="md"
          :loading="isSavingProfile"
          :disable="isSavingProfile || !hasUnsavedChanges"
          @click="handleSaveAvatar"
        >
          <q-tooltip>{{ $t('AVATAR_SAVE_TO_PROFILE') }}</q-tooltip>
        </q-btn>
      </div>
    </q-toolbar>

    <!-- Sticky Preview -->
    <div
      ref="previewRef"
      class="editor-preview"
    >
      <div class="preview-container">
        <img
          v-if="store.currentAvatar"
          :src="store.currentAvatar.toDataUri()"
          alt="avatar preview"
        />
      </div>
    </div>

    <!-- Tabs and Options -->
    <div
      ref="tabsRef"
      class="editor-tabs-container"
    >
      <q-tabs
        v-model="store.selectedTab"
        dense
        mobile-arrows
        outside-arrows
        class="text-grey-8"
        no-caps
        active-color="primary"
        indicator-color="primary"
      >
        <q-tab
          v-for="key in optionKeys"
          :key="key"
          :name="key"
          :label="formatLabel(key)"
          :disable="!hasMultipleOptions(key)"
        />
      </q-tabs>
      <q-separator />
      <q-tab-panels
        v-model="store.selectedTab"
        animated
        transition-prev="slide-down"
        transition-next="slide-up"
        class="editor-options"
      >
        <q-tab-panel
          v-for="key in optionKeys"
          :key="key"
          :name="key"
          class="q-pa-md"
        >
          <div class="options-grid">
            <!-- Grid items for style selection -->
            <template v-if="key === 'style'">
              <div
                v-for="styleName in Object.keys(store.styleCombinations)"
                :key="styleName"
                class="option-item-wrapper"
                :class="{
                  'option-item-active': store.selectedStyleName === styleName,
                }"
                @click="() => store.changeStyle(styleName)"
              >
                <div class="option-item">
                  <div class="option-avatar">
                    <img
                      :src="
                        store.styleCombinations[styleName]?.[0]?.avatar.toDataUri()
                      "
                      alt="avatar"
                    />
                  </div>
                </div>
              </div>
            </template>

            <!-- Grid items for other options -->
            <template v-else>
              <div
                v-for="(combination, ci) in store.optionCombinations[key] || []"
                :key="`${key}-${ci}`"
                class="option-item-wrapper"
                :class="{ 'option-item-active': combination.active }"
                @click="() => handleOptionClick(combination)"
              >
                <div class="option-item">
                  <div class="option-avatar">
                    <img
                      :src="combination.avatar.toDataUri()"
                      alt="avatar"
                    />
                  </div>

                  <!-- Color picker for custom color -->
                  <div
                    v-if="combination.isCustomColor"
                    class="option-color-picker"
                  >
                    <input
                      type="color"
                      :value="
                        '#' + (store.currentStyleOptions[key] || 'ffffff')
                      "
                      class="color-input"
                      @input="
                        (e: InputEvent) =>
                          handleColorChange(
                            key,
                            (e.target as HTMLInputElement).value,
                          )
                      "
                    />
                    <div class="color-wheel-icon" />
                  </div>
                </div>
              </div>
            </template>
          </div>
        </q-tab-panel>
      </q-tab-panels>
    </div>
  </template>
</template>

<style scoped lang="scss">
.avatar-editor-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 0;
}

.loading-state,
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  color: white;
}

.editor-header,
.editor-preview,
.editor-tabs {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  z-index: 100;
}

.editor-header {
  padding: 16px 20px;

  .header-content {
    max-width: 960px;
    margin: 0 auto;
    display: flex;
    justify-content: space-between;
    align-items: center;
    color: white;
  }

  .header-actions {
    display: flex;
    gap: 8px;
  }
}

.editor-toolbar {
  // Line the actions up with the options column below, rather than pinning them
  // to the viewport edges on wide screens.
  max-width: 960px;
  left: 0;
  right: 0;
  margin: 0 auto;
  // Above .editor-preview so the download menu is not clipped by the card.
  z-index: 101;
}

.toolbar-group {
  display: flex;
  align-items: center;
  gap: 4px;
}

.editor-preview {
  padding: 40px 20px;
  display: flex;
  justify-content: center;
  align-items: center;

  .preview-container {
    max-width: min(32vh, 90vw);
    width: 100%;
    border-radius: 20px;
    overflow: hidden;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
    background: white;

    img {
      display: block;
      width: 100%;
      height: auto;
    }
  }
}

.editor-tabs-container {
  background: rgb(255, 255, 255);
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.1);
}

.editor-options {
  background: white;
  min-height: 45svh;
  max-height: calc(100svh - 538px);
  height: 100%;
  overflow-y: auto;

  :deep(.q-tab-panel) {
    max-width: 960px;
    margin: 0 auto;
  }
}

.options-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(88px, 1fr));
  gap: 20px;
  width: 100%;
}

.option-item-wrapper {
  position: relative;
  cursor: pointer;
  padding: 6px;
  transition: all 0.2s ease;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    border-radius: 18px;
    border: 0 solid #1976d2;
    transition: border-width 0.15s ease;
    pointer-events: none;
  }

  &:hover::before {
    border-width: 3px;
  }

  &-active::before {
    border-width: 3px !important;
  }
}

.option-item {
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  background: white;
  aspect-ratio: 1;
  width: 100%;

  .option-avatar {
    width: 100%;
    height: 100%;
    display: block;
    background: white;

    img {
      width: 100%;
      height: 100%;
      display: block;
      object-fit: cover;
    }

    :deep(svg) {
      width: 100%;
      height: 100%;
      display: block;
    }
  }

  .option-color-picker {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    cursor: pointer;

    .color-input {
      position: absolute;
      bottom: 4px;
      right: 4px;
      width: 28px;
      height: 28px;
      opacity: 0;
      cursor: pointer;
    }

    .color-wheel-icon {
      position: absolute;
      bottom: 4px;
      right: 4px;
      width: 28px;
      height: 28px;
      border: 2px solid white;
      border-radius: 50%;
      background: conic-gradient(
        red 0%,
        orange 14.29%,
        yellow 28.57%,
        green 42.86%,
        blue 57.14%,
        indigo 71.43%,
        violet 85.71%,
        red 100%
      );
      pointer-events: none;

      &::after {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background: white;
      }
    }
  }
}

@media (max-width: 768px) {
  .options-grid {
    grid-template-columns: repeat(auto-fill, minmax(72px, 1fr));
    gap: 16px;
  }
}
</style>
